import { useForm } from "react-hook-form";
import { getPrintDateTypeFormValue, getPrintDateTypeOption, LuckyDrawModel, mergeLuckyDrawGeneralSource, restorePrintDateTypeFormValue } from "../../../models/LuckyDraw.model";
import { useSafeFormDateRange } from "src/app/hooks/safe-form-date-range";
import { useGetLuckyDrawDetailGeneralInfo, useSaveLuckyDrawGeneral } from "../../../providers/LuckyDraw.provider";
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

const defaultForm: LuckyDrawModel = {
    category: 'LUCKY_DRAW',
    category_type: { value: 'LUCKY_DRAW', label: 'Lucky Draw' },
    name: '',
    directory_ids: [],
    start_date: '',
    end_date: '',
    is_printable: undefined,
    print_date_type: undefined,
    max_day_to_print: undefined,
    last_print_date: undefined,
    coupon_selection_image: '',
    max_day_to_choose_prize: undefined,
    image: '',
    tnc_eng: '',
    tnc_idn: '',
    consolation_alias: '',
    is_draft: false
}

const masterCampaignType = [{
    value: 'LUCKY_DRAW',
    label: 'Lucky Draw'
}]

export function useLuckyDrawForm1(id?: string, currentDetail?: LuckyDrawModel) {
    const saveLuckyDrawGeneral = useSaveLuckyDrawGeneral()
    const luckyDrawDetail = useGetLuckyDrawDetailGeneralInfo(id, false)
    const form = useForm({ defaultValues: defaultForm, reValidateMode: 'onChange' })
    const { setValue, getValues, clearErrors } = form

    const minPeriod = useSafeFormDateRange({
        startDateKey: 'start_date',
        endDateKey: 'end_date',
        form,
    })

    // Watch only the specific fields we care about
    const { category_type, name, directories, start_date, end_date, max_day_to_print, max_day_to_choose_prize, print_date_type, last_print_date, coupon_selection_image, is_printable } = form.watch();
    // Check if at least one of the required fields is filled
    const isFormFilled = category_type || name || start_date || end_date || directories || max_day_to_print || max_day_to_choose_prize || print_date_type || last_print_date || coupon_selection_image || is_printable !== undefined
    const [luckyDrawDetailStatus, setLuckyDrawDetailStatus] = useState('')
    useEffect(()=>{
        const sourceData = mergeLuckyDrawGeneralSource(currentDetail, luckyDrawDetail.data);
        if (sourceData) {
            const categoryTypeVal = masterCampaignType.find(key=>key.value===sourceData?.category) ?? masterCampaignType[0];
            setValue('category_type', categoryTypeVal);
            if (sourceData?.image) setValue('image', sourceData.image);
            if (sourceData?.coupon_selection_image) setValue('coupon_selection_image', sourceData.coupon_selection_image);
            if (sourceData?.is_printable !== undefined) setValue('is_printable', sourceData.is_printable);
            const printDateTypeOption = getPrintDateTypeFormValue(sourceData);
            if (printDateTypeOption) {
                setValue('print_date_type', printDateTypeOption as unknown as LuckyDrawModel['print_date_type'], { shouldValidate: true });
                clearErrors('print_date_type');
            }
            if (sourceData?.max_day_to_print !== undefined) setValue('max_day_to_print', sourceData.max_day_to_print);
            if (sourceData?.last_print_date) setValue('last_print_date', sourceData.last_print_date);
            setLuckyDrawDetailStatus(sourceData?.status ?? '');
        }
    }, [luckyDrawDetail.data, currentDetail, setValue, clearErrors])

    useEffect(() => {
        const option = getPrintDateTypeOption(print_date_type);
        if (!option) return;
        const currentLabel = typeof print_date_type === 'object' && print_date_type && 'label' in print_date_type
            ? (print_date_type as { label?: string }).label
            : undefined;
        if (typeof print_date_type === 'string' || currentLabel !== option.label) {
            setValue('print_date_type', option as unknown as LuckyDrawModel['print_date_type'], { shouldValidate: true });
        }
        if (option.value === 'FIX_DATE' && !getValues('last_print_date')) {
            setValue('last_print_date', dayjs().format('YYYY-MM-DD'), { shouldValidate: true });
        }
    }, [print_date_type, setValue, getValues])

    useEffect(() => {
        const printable = is_printable === true || (is_printable as unknown) === 'true';
        if (!printable) {
            clearErrors(['print_date_type', 'max_day_to_print', 'last_print_date', 'is_printable']);
            return;
        }
        const sourceData = mergeLuckyDrawGeneralSource(currentDetail, luckyDrawDetail.data);
        const option = restorePrintDateTypeFormValue(getValues('print_date_type'), sourceData);
        if (!option) return;
        setValue('print_date_type', option as unknown as LuckyDrawModel['print_date_type'], { shouldValidate: true });
        clearErrors('print_date_type');
        const lastPrintDate = getValues('last_print_date') || sourceData?.last_print_date || (option.value === 'FIX_DATE' ? dayjs().format('YYYY-MM-DD') : undefined);
        if (lastPrintDate) setValue('last_print_date', lastPrintDate);
        const maxDayToPrint = getValues('max_day_to_print') ?? sourceData?.max_day_to_print;
        if (maxDayToPrint !== undefined && maxDayToPrint !== null) setValue('max_day_to_print', maxDayToPrint);
    }, [is_printable, luckyDrawDetail.data, currentDetail, getValues, setValue, clearErrors])

    const enabledFields = ()=> {
        const fields = [
            'end_date',
            'is_printable',
            'print_date_type',
            'max_day_to_print',
            'last_print_date',
            'coupon_selection_image',
            'max_day_to_choose_prize',
            'image',
            'tnc_eng',
            'tnc_idn',
            'is_draft',
            'consolation_alias'
        ]
        if (luckyDrawDetailStatus === 'ACTIVE') {
            return fields;
        }
        return [...fields, 'category', 'name', 'directory_ids', 'directories', 'start_date']
    }

    return ({
        saveLuckyDrawGeneral,
        luckyDrawDetail,
        form,
        masterCampaignType,
        minPeriod,
        isFormFilled,
        enabledFields
    })
}